// hooks/useFetchPhotos.ts
import { useQuery } from '@tanstack/react-query';
import { api } from 'api/v1/api';
import { useEffect, useMemo } from 'react';

interface PhotoData {
  token: string;
  status: number;
  headers: Record<string, string>;
  ok: boolean;
  blob?: Blob;
  error?: string;
}

export type PhotoDataWithUrl = PhotoData & {
  blobUrl?: string;
};

type UseFetchPhotosOptions = {
  /** Зафиксировать статусы после первой загрузки (страница редактирования) */
  freezeStatuses?: boolean;
};

/** Токены, по которым get_image вернул 403/404 - не запрашиваем повторно в этой сессии. */
const permanentlyFailedTokens = new Set<string>();

const isPermanentPhotoError = (status: number): boolean =>
  status === 403 || status === 404 || status === 410;

export const markPhotoTokenFailed = (token: string): void => {
  if (token) permanentlyFailedTokens.add(token);
};

export const isPhotoTokenFailed = (token: string): boolean =>
  permanentlyFailedTokens.has(token);

export const getPhotoHeaderStatus = (headers?: Record<string, string>): string =>
  headers?.['x-photo-status'] || headers?.['X-Photo-Status'] || '';

/**
 * Хук загрузки фото. Blob хранится в React Query cache; blob URL создается
 * локально в каждом подписчике и отзывается только при размонтировании этого экземпляра.
 */
export const useFetchPhotos = (tokens: string[], options?: UseFetchPhotosOptions) => {
  const freezeStatuses = options?.freezeStatuses ?? false;
  const validTokens = tokens.filter(
    (token): token is string => Boolean(token) && !permanentlyFailedTokens.has(token),
  );
  const queryKey = freezeStatuses
    ? (['photos-full-frozen', validTokens] as const)
    : (['photos-full', validTokens] as const);

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<PhotoData[]> => {
      const results = await Promise.all(
        validTokens.map(async (token) => {
          try {
            const response = await api.get(`/get_image/${token}`, {
              responseType: 'blob',
              skipGlobalErrorHandler: true,
            });

            const headers = { ...response.headers } as Record<string, string>;
            const blob = response.data as Blob;

            return {
              token,
              status: response.status,
              headers,
              blob,
              ok: true,
            };
          } catch (error: any) {
            if (error.response) {
              const status = error.response.status as number;
              if (isPermanentPhotoError(status)) {
                permanentlyFailedTokens.add(token);
              }

              const headers: Record<string, string> = {
                ...(error.response.headers || {}),
              };

              let blob: Blob | undefined;

              if (error.response.data instanceof Blob) {
                blob = error.response.data;
              }

              return {
                token,
                status,
                headers,
                blob,
                ok: false,
                error: error.message,
              };
            }

            return {
              token,
              status: 0,
              headers: {},
              ok: false,
              error: error?.message || 'Network error',
            };
          }
        }),
      );

      return results;
    },
    enabled: validTokens.length > 0,
    retry: false,
    staleTime: freezeStatuses ? Infinity : 5 * 60 * 1000,
    refetchInterval: false,
    refetchOnMount: !freezeStatuses,
    refetchOnWindowFocus: false,
    refetchOnReconnect: !freezeStatuses,
  });

  const blobUrls = useMemo(() => {
    if (!query.data) return [] as (string | undefined)[];
    return query.data.map((item) =>
      item.blob ? URL.createObjectURL(item.blob) : undefined,
    );
  }, [query.data]);

  useEffect(() => {
    return () => {
      blobUrls.forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [blobUrls]);

  const dataWithUrls = useMemo((): PhotoDataWithUrl[] | undefined => {
    if (!query.data) return undefined;
    return query.data.map((item, index) => ({
      ...item,
      blobUrl: blobUrls[index],
    }));
  }, [query.data, blobUrls]);

  return {
    ...query,
    data: dataWithUrls,
  };
};
